import React from 'react';
import { useTranslation } from 'react-i18next';
import { Hash, Info } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faImages, faFont, faIcons, faPalette, faShapes, faPictureInPicture,
  faWandMagicSparkles, faTableCellsLarge, faObjectGroup, faLayerGroup, faQrcode,
} from '@fortawesome/free-solid-svg-icons';
import { SIDEBAR_TOOL_FEATURE_KEYS, type SidebarToolId } from '@customedge/shared';
import { useDesignStore } from '../../store/designStore';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';

/** Adapts a Font Awesome icon to the `size`/`strokeWidth` prop shape the lucide-based `Hash`/`Info` icons still use here. */
function faAdapter(icon: IconDefinition) {
  return function FaAdapted({ size }: { size?: number; strokeWidth?: number }) {
    return <FontAwesomeIcon icon={icon} style={{ width: size ?? 22, height: size ?? 22 }} />;
  };
}

/** Per-item accent color, applied to the icon only (label text and active/hover state stay on the shared --selection token). */
export const SIDEBAR_TOOL_COLORS: Partial<Record<SidebarToolId, string>> = {
  text:       '#A8D8EA', // Sky Mist
  upload:     '#D4B8E8', // Lilac
  clipart:    '#B8E8D4', // Mint
  artwork:    '#F4C2C2', // Blush
  shapes:     '#F6E3B4', // Vanilla
  background: '#B4DDE0', // Powder Teal
  effects:    '#E8C4E8', // Orchid Mist
  barcode:    '#F0D4A8', // Sand
  template:   '#C4E8C4', // Sage
  mydesigns:  '#C8C4EA', // Periwinkle
  layers:     '#F0C4C4', // Rose Mist
  qrcode:     '#B8D0EA', // Ice Blue
};

export const SIDEBAR_TOOLS = [
  { id: 'text',       labelKey: 'sidebar.text',       Icon: faAdapter(faFont)               },
  { id: 'upload',     labelKey: 'sidebar.images',     Icon: faAdapter(faImages)             },
  { id: 'clipart',    labelKey: 'sidebar.clipart',    Icon: faAdapter(faIcons)              },
  { id: 'artwork',    labelKey: 'sidebar.artworks',   Icon: faAdapter(faPalette)            },
  { id: 'shapes',     labelKey: 'sidebar.shapes',     Icon: faAdapter(faShapes)             },
  { id: 'background', labelKey: 'sidebar.background', Icon: faAdapter(faPictureInPicture)   },
  { id: 'effects',    labelKey: 'sidebar.effects',    Icon: faAdapter(faWandMagicSparkles)  },
  { id: 'barcode',    labelKey: 'sidebar.barcode',    Icon: Hash                            },
  { id: 'template',   labelKey: 'sidebar.templates',  Icon: faAdapter(faTableCellsLarge)    },
  { id: 'mydesigns',  labelKey: 'sidebar.myDesigns',  Icon: faAdapter(faObjectGroup)        },
  { id: 'layers',     labelKey: 'sidebar.layers',     Icon: faAdapter(faLayerGroup)         },
  { id: 'qrcode',     labelKey: 'sidebar.qrcode',     Icon: faAdapter(faQrcode)             },
] as const;

// Re-export so callers importing SidebarToolId from this module still work.
export type { SidebarToolId };
// ActiveToolId extends SidebarToolId with 'info' (gated by infoPanel.enabled)
// and 'edit' — neither is part of the configurable tools allow-list. 'edit'
// has no sidebar icon of its own; it's only ever set programmatically when a
// layer is clicked in RightPanel's LayerPanel (see LeftToolPanel.tsx), which
// swaps the left panel from "Add" tools into the Text/Image edit panel.
export type ActiveToolId = SidebarToolId | 'info' | 'edit';

interface LeftSidebarProps {
  activeTool: ActiveToolId | null;
  onToolSelect: (id: ActiveToolId) => void;
  /** Shown as a badge on the "layers" icon — count of objects on the active canvas. */
  layerCount?: number;
}

export function LeftSidebar({ activeTool, onToolSelect, layerCount = 0 }: LeftSidebarProps) {
  const { t } = useTranslation();
  const layoutConfig = useDesignStore(s => s.layoutConfig);
  const features = useFeatureFlags();
  const visibleTools = SIDEBAR_TOOLS
    .filter(tool => {
      if (!layoutConfig.tools.includes(tool.id)) return false;
      // Still loading → don't hide yet (avoids a hidden-then-visible flash).
      return features === null || features.has(SIDEBAR_TOOL_FEATURE_KEYS[tool.id]);
    })
    // Stable sort by the matching Feature row's admin-grid drag-and-drop
    // `position` — `.filter()` above preserves `SIDEBAR_TOOLS`' fixed order,
    // which this falls back to while `features` is still loading.
    .sort((a, b) => {
      if (features === null) return 0;
      const pa = features.get(SIDEBAR_TOOL_FEATURE_KEYS[a.id])?.position ?? 0;
      const pb = features.get(SIDEBAR_TOOL_FEATURE_KEYS[b.id])?.position ?? 0;
      return pa - pb;
    });

  const infoPanelEnabled = layoutConfig.infoPanel?.enabled;

  const renderToolButton = (id: ActiveToolId, Icon: React.ElementType, label: string, badge?: React.ReactNode) => {
    const isActive = activeTool === id;
    const iconColor = SIDEBAR_TOOL_COLORS[id as SidebarToolId];
    return (
      <button
        key={id}
        onClick={() => onToolSelect(id)}
        title={label}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          padding: '12px 6px',
          minHeight: 68,
          border: 'none',
          borderLeft: isActive ? '3px solid var(--selection)' : '3px solid transparent',
          background: isActive ? 'var(--selection-tint)' : 'transparent',
          cursor: 'pointer',
          color: isActive ? 'var(--selection-text)' : 'rgba(0,0,0,0.87)',
          width: '100%',
          transition: 'background 0.15s, color 0.15s',
        }}
      >
        <span style={{ position: 'relative', display: 'inline-flex', color: iconColor ?? 'inherit' }}>
          <Icon size={22} strokeWidth={1.75} />
          {badge}
        </span>
        <span style={{
          fontSize: 12,
          fontWeight: 500,
          textAlign: 'center',
          lineHeight: 1.2,
          maxWidth: 72,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}>
          {label}
        </span>
      </button>
    );
  };

  return (
    <div
    className="left-sidebar-inner"
     style={{
      width: 96,
      flexShrink: 0,
      background: '#ffffff',
      borderRight: '1px solid #e2e8f0',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      overflowX: 'hidden',
    }}>
      {/* Info panel button — first in order, only shown when layout enables infoPanel */}
      {infoPanelEnabled && renderToolButton('info', Info, t('sidebar.info'))}

      {visibleTools.map(tool => {
        const layerBadge = tool.id === 'layers' && layerCount > 0 ? (
          <span style={{
            position: 'absolute',
            top: -7,
            right: -12,
            minWidth: 18,
            height: 18,
            padding: '0 4px',
            borderRadius: 9,
            background: 'var(--selection)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '18px',
            textAlign: 'center',
          }}>
            {layerCount > 99 ? '99+' : layerCount}
          </span>
        ) : undefined;
        return renderToolButton(tool.id, tool.Icon, t(tool.labelKey), layerBadge);
      })}
    </div>
  );
}
